import { call, put, takeEvery } from "redux-saga/effects";

import {
  CREATE_LICENSE_USER,
  CREATE_SUBSCRIPTION,
  GET_SUBSCRIPTIONS,
  GET_SUBSCRIPTION_BILLINGS,
  CHECK_SUBSCRIPTION_STATUS,
  DELETE_SUBSCRIPTION,
  GET_PRODUCTS,
  MARK_SUBSCRIPTION_PAID,
  FREEZE_UNFREEZE_SUBSCRIPTION,
} from "./actionTypes";

import {
  createLicenseUserSuccess,
  createLicenseUserError,
  createSubscriptionSuccess,
  createSubscriptionError,
  getSubscriptionsSuccess,
  getSubscriptionsError,
  getSubscriptionBillingsSuccess,
  getSubscriptionBillingsError,
  checkSubscriptionStatusSuccess,
  checkSubscriptionStatusError,
  deleteSubscriptionSuccess,
  deleteSubscriptionError,
  getProductsSuccess,
  getProductsError,
  markSubscriptionAsPaidSuccess,
  markSubscriptionAsPaidError,
  freezeUnfreezeSubscriptionSuccess,
  freezeUnfreezeSubscriptionError,
  getSubscriptions,
  getSubscriptionBillings,
} from "./actions";

import * as api from "../../apis/license";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../notifications/actions";
import { fetchClientDetails } from "store/client/actions";

// Create License User
function* createLicenseUserSaga({ payload }) {
  try {
    const { customerId } = payload;
    const data = yield call(api.createLicenseUser, customerId);
    yield put(createLicenseUserSuccess(data));
    yield put(showSuccessNotification("License user created successfully"));
  } catch (error) {
    yield put(
      createLicenseUserError(error.message || "Failed to create license user")
    );
    yield put(
      showErrorNotification(error.message || "Failed to create license user")
    );
  }
}

// Create Subscription
function* createSubscriptionSaga({ payload }) {
  try {
    const { customerId, payload: subscriptionData } = payload;
    const data = yield call(
      api.createSubscription,
      customerId,
      subscriptionData
    );
    yield put(createSubscriptionSuccess(data));
    yield put(showSuccessNotification("Subscription created successfully"));
    yield put(fetchClientDetails(customerId));
  } catch (error) {
    yield put(
      createSubscriptionError(error.message || "Failed to create subscription")
    );
    yield put(
      showErrorNotification(error.message || "Failed to create subscription")
    );
  }
}

// Get Subscriptions
function* getSubscriptionsSaga({ payload }) {
  try {
    const { customerId, page, limit } = payload;
    const data = yield call(api.getSubscriptions, customerId, page, limit);
    yield put(getSubscriptionsSuccess(data));
  } catch (error) { 
    yield put(
      getSubscriptionsError(error.message || "Failed to fetch subscriptions")
    );
    yield put(
      showErrorNotification(error.message || "Failed to fetch subscriptions")
    );
  }
}

function* getSubscriptionBillingsSaga({ payload }) {
  try {
    const { subscriptionId, page, limit } = payload;
    const data = yield call(
      api.getSubscriptionBillings,
      subscriptionId,
      page,
      limit
    );
    yield put(getSubscriptionBillingsSuccess(data));
  } catch (error) {
    yield put(
      getSubscriptionBillingsError(
        error.message || "Failed to fetch billing history"
      )
    );
    yield put(
      showErrorNotification(error.message || "Failed to fetch billing history")
    );
  }
}

// Check Subscription Status
function* checkSubscriptionStatusSaga({ payload }) {
  try {
    const { customerId } = payload;
    const data = yield call(api.checkSubscriptionStatus, customerId);
    yield put(checkSubscriptionStatusSuccess(data));
  } catch (error) {
    yield put(
      checkSubscriptionStatusError(
        error.message || "Failed to check subscription status"
      )
    );
    yield put(
      showErrorNotification(
        error.message || "Failed to check subscription status"
      )
    );
  }
}

// Delete Subscription
function* deleteSubscriptionSaga({ payload }) {
  try {
    const { customerId, subscriptionId } = payload;
    const data = yield call(api.deleteSubscription, customerId, subscriptionId);
    yield put(deleteSubscriptionSuccess(data));
    yield put(showSuccessNotification("Subscription deleted successfully"));
  } catch (error) {
    yield put(
      deleteSubscriptionError(error.message || "Failed to delete subscription")
    );
    yield put(
      showErrorNotification(error.message || "Failed to delete subscription")
    );
  }
}

// Get Products
function* getProductsSaga({ payload }) {
  try {
    const { page, limit } = payload;
    const data = yield call(api.getProducts, page, limit);
    yield put(getProductsSuccess(data));
  } catch (error) {
    yield put(getProductsError(error.message || "Failed to fetch products"));
    yield put(
      showErrorNotification(error.message || "Failed to fetch products")
    );
  }
}

// Mark Subscription as Paid
function* markSubscriptionAsPaidSaga({ payload }) {
  try {
    const { subscriptionId, customerId, payload: paidData } = payload;
    const data = yield call(
      api.markSubscriptionAsPaid,
      customerId,
      subscriptionId,
      paidData
    );
    yield put(markSubscriptionAsPaidSuccess(data));
    yield put(
      showSuccessNotification("Subscription marked as paid successfully")
    );
  } catch (error) {
    yield put(
      markSubscriptionAsPaidError(
        error.message || "Failed to mark subscription as paid"
      )
    );
    yield put(
      showErrorNotification(
        error.message || "Failed to mark subscription as paid"
      )
    );
  }
}

// Freeze/Unfreeze Subscription
function* freezeUnfreezeSubscriptionSaga({ payload }) {
  try {
    const { customerId, subscriptionId, data, page = 1, limit = 10 } = payload;
    const response = yield call(
      api.freezeUnfreezeSubscription,
      customerId,
      subscriptionId,
      data
    );
    yield put(freezeUnfreezeSubscriptionSuccess(response.data));
    yield put(
      showSuccessNotification(
        `Subscription ${data.is_freezed ? "frozen" : "unfrozen"} successfully`
      )
    );

    // Refresh subscription data
    yield put(getSubscriptions({customerId}));

  } catch (error) {
    yield put(
      freezeUnfreezeSubscriptionError(
        error.response?.data?.message || error.message
      )
    );
    yield put(
      showErrorNotification(error.response?.data?.message || error.message)
    );
  }
}

function* licenseSaga() {
  yield takeEvery(CREATE_LICENSE_USER, createLicenseUserSaga);
  yield takeEvery(CREATE_SUBSCRIPTION, createSubscriptionSaga);
  yield takeEvery(GET_SUBSCRIPTIONS, getSubscriptionsSaga);
  yield takeEvery(GET_SUBSCRIPTION_BILLINGS, getSubscriptionBillingsSaga);
  yield takeEvery(CHECK_SUBSCRIPTION_STATUS, checkSubscriptionStatusSaga);
  yield takeEvery(DELETE_SUBSCRIPTION, deleteSubscriptionSaga);
  yield takeEvery(GET_PRODUCTS, getProductsSaga);
  yield takeEvery(MARK_SUBSCRIPTION_PAID, markSubscriptionAsPaidSaga);
  yield takeEvery(FREEZE_UNFREEZE_SUBSCRIPTION, freezeUnfreezeSubscriptionSaga);
}

export default licenseSaga;
